<script setup lang="ts">
import { ref, onMounted, onUnmounted, nextTick, watch } from "vue";
import { useRoute } from "vue-router";
import {
  DatabaseIcon,
  InboxIcon,
  AddIcon,
  ArrowUpRightIcon,
  MenuIcon,
  CloseIcon,
  LogOutIcon,
  ShieldIcon,
  LinkIcon,
} from "./icons";
import { useSession, api, errorText } from "./api";
import BrandLogo from "./components/BrandLogo.vue";
import DatasetDirectory from "./components/DatasetDirectory.vue";
const session = useSession(),
  route = useRoute(),
  menu = ref(false),
  auth = ref({ logto: false, developmentLogin: false }),
  error = ref(""),
  busy = ref(false);
const mobile = ref(window.matchMedia("(max-width: 980px)").matches),
  sidebar = ref<HTMLElement>(),
  menuTrigger = ref<HTMLButtonElement>();
const media = window.matchMedia("(max-width: 980px)");
const resize = () => {
  mobile.value = media.matches;
};
onMounted(() => media.addEventListener("change", resize));
onUnmounted(() => media.removeEventListener("change", resize));
watch(menu, async (open) => {
  await nextTick();
  if (open) sidebar.value?.querySelector<HTMLElement>("a,button")?.focus();
  else if (mobile.value) menuTrigger.value?.focus();
});
function drawerKey(event: KeyboardEvent) {
  if (!mobile.value || !menu.value) return;
  if (event.key === "Escape") {
    event.preventDefault();
    menu.value = false;
  }
  if (event.key !== "Tab") return;
  const elements = [
    ...sidebar.value!.querySelectorAll<HTMLElement>("a,button,summary"),
  ];
  const first = elements[0],
    last = elements.at(-1);
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last?.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first?.focus();
  }
}
onMounted(async () => {
  try {
    auth.value = await api("/auth/config");
  } catch (e) {
    error.value = errorText(e);
  }
  try {
    await session.refresh();
  } catch (e) {
    error.value = errorText(e);
    session.ready = true;
  }
});
watch(
  () => route.fullPath,
  () => (menu.value = false),
);
async function devLogin() {
  busy.value = true;
  error.value = "";
  try {
    await api("/auth/dev", { method: "POST" });
    await session.refresh();
  } catch (e) {
    error.value = errorText(e);
  } finally {
    busy.value = false;
  }
}
</script>
<template>
  <RouterView v-if="route.meta.public" :key="route.fullPath" />
  <div v-else-if="!session.ready" class="boot" role="status">
    Yawhub åbner dit workspace…
  </div>
  <div v-else-if="!session.user" class="login-layout">
    <main class="login-panel">
      <a class="brand" href="/" aria-label="Yawhub"><BrandLogo /></a>
      <div class="login-copy">
        <h1>Et sted til data.<br />Plads til indsigt.</h1>
        <p>
          Saml dine kilder, find mønstrene og lad de vigtige ændringer komme til
          dig.
        </p>
        <a
          v-if="auth.logto"
          class="button primary"
          :href="
            route.path === '/connect' || route.path.startsWith('/approvals/')
              ? '/auth/login?returnTo=' + encodeURIComponent(route.fullPath)
              : '/auth/login'
          "
          >Log ind <ArrowUpRightIcon :size="18"
        /></a>
        <a
          v-if="auth.logto"
          class="button secondary"
          :href="
            route.path === '/connect' || route.path.startsWith('/approvals/')
              ? '/auth/register?returnTo=' + encodeURIComponent(route.fullPath)
              : '/auth/register'
          "
          >Registrér</a
        >
        <p v-else class="notice">
          Forbind din self-hosted Logto-applikation for at aktivere login.
        </p>
        <button
          v-if="auth.developmentLogin"
          class="button secondary"
          :disabled="busy"
          @click="devLogin"
        >
          {{ busy ? "Åbner…" : "Åbn lokalt udviklingsmiljø" }}
        </button>
        <p v-if="auth.developmentLogin" class="muted small">
          Lokal udviklingskonto. Slået fra i produktion.
        </p>
        <p v-if="error" class="error" role="alert">{{ error }}</p>
      </div>
      <div class="login-footer">
        <ShieldIcon :size="18" /> Dit workspace. Din self-hosted identitet.
      </div>
    </main>
    <aside class="login-note">
      <img
        class="login-brand-mark"
        src="/brand/yawhub.png"
        alt=""
        width="301"
        height="301"
      />
      <h2>Fra kilde<br />til klarhed.</h2>
      <p>Importér. Undersøg. Følg op.</p>
      <div class="login-lines">
        <span>Datasæt</span><span>Dashboards</span><span>Handlinger</span>
      </div>
    </aside>
  </div>
  <div v-else class="app-shell">
    <aside
      ref="sidebar"
      class="sidebar"
      :class="{ open: menu }"
      :inert="mobile && !menu"
      @keydown="drawerKey"
      aria-label="Hovednavigation"
    >
      <RouterLink to="/" class="brand" aria-label="Yawhub"
        ><BrandLogo /></RouterLink
      ><button
        class="icon-button sidebar-close"
        aria-label="Luk menu"
        @click="menu = false"
      >
        <CloseIcon />
      </button>
      <div class="workspace-chip">
        <span class="avatar">{{
          session.user.name.slice(0, 1).toUpperCase()
        }}</span
        ><span
          ><strong>{{ session.user.name }}</strong
          ><small>Personligt workspace</small></span
        >
      </div>
      <nav>
        <RouterLink
          to="/"
          :class="{
            active: route.path === '/',
          }"
          ><DatabaseIcon :size="19" />Datasæt</RouterLink
        ><DatasetDirectory /><RouterLink
          to="/inbox"
          :class="{ active: route.path === '/inbox' }"
          ><InboxIcon :size="19" />Indbakke</RouterLink
        >
        <RouterLink
          to="/connections"
          :class="{ active: ['/connections', '/connect'].includes(route.path) }"
          ><LinkIcon :size="19" />AI & MCP</RouterLink
        >
      </nav>
      <RouterLink to="/new" class="sidebar-create"
        ><AddIcon :size="18" />Tilføj datasæt</RouterLink
      >
      <div class="sidebar-bottom">
        <div class="workspace-status">
          <span class="status-dot" />Forbundet til dit workspace
        </div>
        <button class="text-button" @click="session.logout">
          <LogOutIcon :size="17" />Log ud
        </button>
      </div>
    </aside>
    <button
      v-if="menu"
      class="scrim"
      aria-label="Luk menu"
      @click="menu = false"
    />
    <div class="workspace-main">
      <header class="topbar">
        <button
          ref="menuTrigger"
          class="icon-button mobile-menu"
          aria-label="Åbn menu"
          :aria-expanded="menu"
          @click="menu = true"
        >
          <MenuIcon /></button
        ><RouterLink to="/" class="mobile-brand" aria-label="Yawhub"
          ><img
            src="/brand/yawhub.png"
            alt=""
            width="301"
            height="301" /></RouterLink
        ><span
          >Mit workspace <span class="topbar-separator">/</span
          ><span class="muted">{{
            route.path === "/inbox"
              ? "Indbakke"
              : ["/connections", "/connect"].includes(route.path)
                ? "AI & MCP"
                : route.path === "/new"
                  ? "Nyt datasæt"
                  : "Data & indsigt"
          }}</span></span
        ><span class="topbar-user">{{ session.user.name }}</span>
      </header>
      <main id="main-content" class="main-content">
        <RouterView :key="route.path" />
      </main>
      <footer class="app-footer">
        <span>Yawhub</span><span>Data med sammenhæng.</span>
      </footer>
    </div>
  </div>
</template>
<style scoped lang="scss" src="./styles/App.scss"></style>
